import { AppSidebar } from '@components/shared/sidebar.component';
import { SIDEBAR_COOKIE_NAME, SidebarInset, SidebarProvider } from '@components/ui/sidebar';
import { MOBILE_BREAKPOINT } from '@hooks/mobile.hook';
import { cn } from '@lib/utils';
import Cookies from 'js-cookie';
import React from 'react';

type MainLayoutColumnsStartAt = 'sm' | 'md' | 'lg' | 'xl';
type MainLayoutColumnsStartAtContainer = '56rem' | '60rem' | '64rem' | '68rem' | '80rem';

type MainLayoutBreakpointClasses = {
  aside: string;
  content: string;
  row: string;
};

const MAIN_LAYOUT_BREAKPOINT_CLASSES: Record<MainLayoutColumnsStartAt, MainLayoutBreakpointClasses> = {
  lg: {
    aside: 'lg:max-w-[30%]',
    content: 'lg:max-w-[70%]',
    row: 'lg:flex-row',
  },
  md: {
    aside: 'md:max-w-[30%]',
    content: 'md:max-w-[70%]',
    row: 'md:flex-row',
  },
  sm: {
    aside: 'sm:max-w-[30%]',
    content: 'sm:max-w-[70%]',
    row: 'sm:flex-row',
  },
  xl: {
    aside: 'xl:max-w-[30%]',
    content: 'xl:max-w-[70%]',
    row: 'xl:flex-row',
  },
};

const MAIN_LAYOUT_CONTAINER_BREAKPOINT_CLASSES: Record<MainLayoutColumnsStartAtContainer, MainLayoutBreakpointClasses> =
  {
    '56rem': {
      aside: '@min-[56rem]/main:max-w-[30%]',
      content: '@min-[56rem]/main:max-w-[70%]',
      row: '@min-[56rem]/main:flex-row',
    },
    '60rem': {
      aside: '@min-[60rem]/main:max-w-[30%]',
      content: '@min-[60rem]/main:max-w-[70%]',
      row: '@min-[60rem]/main:flex-row',
    },
    '64rem': {
      aside: '@min-[64rem]/main:max-w-[30%]',
      content: '@min-[64rem]/main:max-w-[70%]',
      row: '@min-[64rem]/main:flex-row',
    },
    '68rem': {
      aside: '@min-[68rem]/main:max-w-[30%]',
      content: '@min-[68rem]/main:max-w-[70%]',
      row: '@min-[68rem]/main:flex-row',
    },
    '80rem': {
      aside: '@min-[80rem]/main:max-w-[30%]',
      content: '@min-[80rem]/main:max-w-[70%]',
      row: '@min-[80rem]/main:flex-row',
    },
  };

type MainLayoutProps = {
  aside?: React.ReactNode;
  asideClassName?: string;
  children: React.ReactNode;
  className?: string;
  columnsStartAt?: MainLayoutColumnsStartAt;
  columnsStartAtContainer?: MainLayoutColumnsStartAtContainer;
  contentClassName?: string;
  fullWidth?: boolean;
};

export const MainLayout: React.FC<MainLayoutProps> = (props) => {
  const {
    aside,
    asideClassName,
    children,
    className,
    columnsStartAt,
    columnsStartAtContainer,
    contentClassName,
    fullWidth,
  } = props;
  const defaultOpen = Cookies.get(SIDEBAR_COOKIE_NAME) === 'true';
  const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
  const viewportBreakpointClasses = columnsStartAt ? MAIN_LAYOUT_BREAKPOINT_CLASSES[columnsStartAt] : undefined;
  const containerBreakpointClasses = columnsStartAtContainer
    ? MAIN_LAYOUT_CONTAINER_BREAKPOINT_CLASSES[columnsStartAtContainer]
    : undefined;
  const breakpointClasses = containerBreakpointClasses ?? viewportBreakpointClasses;

  return (
    <SidebarProvider
      defaultOpen={defaultOpen && !isMobile}
      style={{ '--sidebar-width-icon': '52px' } as React.CSSProperties}
    >
      <AppSidebar />
      <SidebarInset>
        <div className="@container/main w-full">
          <div
            className={cn(
              'max-w-[1600px] mx-auto w-full p-4 flex flex-col gap-4',
              breakpointClasses?.row ?? 'sm:flex-row',
              className,
            )}
          >
            <div
              className={cn(
                'flex min-w-0 flex-col w-full',
                !fullWidth && (breakpointClasses?.content ?? 'md:max-w-[70%]'),
                contentClassName,
              )}
            >
              {children}
            </div>
            {!fullWidth && aside && (
              <aside
                aria-label="Widgets"
                className={cn(
                  'flex min-w-0 w-full flex-col gap-4 overflow-hidden',
                  breakpointClasses?.aside ?? 'md:max-w-[30%]',
                  asideClassName,
                )}
                role="complementary"
              >
                {aside}
              </aside>
            )}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};
